/**
 * `checkDomainReadinessCrawlerAccess` — dashboard-side thin client for
 * `POST /v1/domain-readiness` scoped to a single signal: `crawler_access`.
 *
 * ADR-0207 Slice 2 needs to surface a WAF-block warning in the dashboard
 * (setup guide + scan panel). The full readiness pipeline the marketing
 * scan uses already returns `signals.crawler_access` on this public
 * endpoint (CORS allow-list already includes the dashboard origins —
 * see `services/api/src/http/middleware/cors.ts` SIGN_IN_CORS_ALLOW_LIST).
 * Reusing the same endpoint avoids duplicating the `crawler_blocked`
 * derivation in a second surface and keeps `services/api` untouched.
 *
 * Wire posture mirrors `apps/marketing/src/lib/domain-readiness.ts`:
 *   - `credentials: 'omit'` — the endpoint mints no cookie and reads
 *     no cookie; omitting avoids attaching any future cookie set
 *     elsewhere on the API origin.
 *   - Success is explicitly `res.status === 200`; anything else is a
 *     silent `null` (the banner is additive; a failed check must never
 *     block the surface it sits above).
 *   - The response shape is validated leniently — an unknown /
 *     out-of-enum `crawler_access` collapses to `'unknown'`, never to
 *     an error state.
 */

import { API_ORIGIN } from '../app/env.js';

/** ADR-0207 Slice 0 — HTTP-response-derived crawler reachability. */
export type CrawlerAccess = 'open' | 'blocked' | 'unknown';

/** Tri-state agent-readiness signal (shared with marketing). */
export type ReadinessTri = 'present' | 'absent' | 'unknown';

/** ADR-0198 — robots.txt classification. */
export type AiAccessSignal = 'open' | 'partial' | 'blocked' | 'unknown';

/** Per-bot robots.txt classification used by the evidence drawer. */
export type RobotsBotStatus = 'allowed' | 'blocked' | 'not_mentioned';

/**
 * The subset of `POST /v1/domain-readiness`.signals the readiness page
 * reads. All fields optional — the server shape is layered leniently so
 * an older API that pre-dates a signal still decodes.
 */
export interface ReadinessSignalsBundle {
  readonly crawler_access?: CrawlerAccess | undefined;
  readonly ai_access?: AiAccessSignal | undefined;
  readonly agent_card?: ReadinessTri | undefined;
  readonly llms_txt?: ReadinessTri | undefined;
  readonly robots_txt?: ReadinessTri | undefined;
  readonly sitemap?: ReadinessTri | undefined;
  readonly structured_data?: ReadinessTri | undefined;
  readonly mcp?: ReadinessTri | undefined;
  readonly api_catalog?: ReadinessTri | undefined;
  /** ADR-0223 — ARD well-known catalog. */
  readonly ard?: ReadinessTri | undefined;
  /** ADR-0223 — UCP business profile. */
  readonly ucp?: ReadinessTri | undefined;
  /** ADR-0225 — informational "charges agents (x402)" tri-state. */
  readonly x402?: ReadinessTri | undefined;
  /** ADR-0225 — closed sub-kind when `x402 === 'present'`. */
  readonly x402_kind?: 'x402' | 'pay_per_crawl' | undefined;
  /** ADR-0252 — informational PAP discovery file tri-state. */
  readonly pap?: ReadinessTri | undefined;
  readonly content_signals?: ReadinessTri | undefined;
  readonly ai_bots_status?: Readonly<Record<string, RobotsBotStatus>> | undefined;
  readonly checked_at?: string | undefined;
}

export type FetchLike = (
  input: string | URL | Request,
  init?: RequestInit,
) => Promise<Response>;

export interface CheckCrawlerAccessDeps {
  readonly fetch_impl?: FetchLike;
  /** Test-only URL override. Production reads from `env.ts`. */
  readonly api_url?: string;
}

/**
 * Fire `POST /v1/domain-readiness` and return just `signals.crawler_access`.
 *
 * Returns `null` when the endpoint didn't answer usefully (rate-limited,
 * network failure, malformed body). Callers treat `null` the same as
 * `'unknown'`: nothing to warn about.
 */
export async function checkDomainReadinessCrawlerAccess(
  domain: string,
  deps: CheckCrawlerAccessDeps = {},
): Promise<CrawlerAccess | null> {
  const fetch_fn = deps.fetch_impl ?? globalThis.fetch.bind(globalThis);
  const url = deps.api_url ?? `${API_ORIGIN}/v1/domain-readiness`;
  const trimmed = domain.trim().toLowerCase();
  if (trimmed.length === 0) return null;
  try {
    const res = await fetch_fn(url, {
      method: 'POST',
      credentials: 'omit',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ domain: trimmed }),
    });
    if (res.status !== 200) return null;
    let parsed: unknown;
    try {
      parsed = await res.json();
    } catch {
      return null;
    }
    return extractCrawlerAccess(parsed);
  } catch {
    return null;
  }
}

/**
 * Lenient narrow — reads only `signals.crawler_access` off an otherwise
 * untrusted shape. Any missing / out-of-enum value collapses to `null`
 * so the caller falls back to "nothing to warn about".
 */
export function extractCrawlerAccess(value: unknown): CrawlerAccess | null {
  if (value === null || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  const signals = v['signals'];
  if (signals === null || typeof signals !== 'object') return null;
  const s = signals as Record<string, unknown>;
  const raw = s['crawler_access'];
  if (raw === 'open' || raw === 'blocked' || raw === 'unknown') return raw;
  return null;
}

/** `POST /v1/domain-readiness` — the full signal bundle. Returns `null` on error. */
export async function checkDomainReadinessSignals(
  domain: string,
  deps: CheckCrawlerAccessDeps = {},
): Promise<ReadinessSignalsBundle | null> {
  const fetch_fn = deps.fetch_impl ?? globalThis.fetch.bind(globalThis);
  const url = deps.api_url ?? `${API_ORIGIN}/v1/domain-readiness`;
  const trimmed = domain.trim().toLowerCase();
  if (trimmed.length === 0) return null;
  try {
    const res = await fetch_fn(url, {
      method: 'POST',
      credentials: 'omit',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ domain: trimmed }),
    });
    if (res.status !== 200) return null;
    let parsed: unknown;
    try {
      parsed = await res.json();
    } catch {
      return null;
    }
    return extractReadinessSignals(parsed);
  } catch {
    return null;
  }
}

function narrowTri(v: unknown): ReadinessTri | undefined {
  return v === 'present' || v === 'absent' || v === 'unknown' ? v : undefined;
}

function narrowAiAccess(v: unknown): AiAccessSignal | undefined {
  return v === 'open' || v === 'partial' || v === 'blocked' || v === 'unknown' ? v : undefined;
}

function narrowBotStatus(v: unknown): RobotsBotStatus | undefined {
  return v === 'allowed' || v === 'blocked' || v === 'not_mentioned' ? v : undefined;
}

function narrowBotMap(v: unknown): Record<string, RobotsBotStatus> | undefined {
  if (v === null || typeof v !== 'object') return undefined;
  const out: Record<string, RobotsBotStatus> = {};
  for (const [k, raw] of Object.entries(v as Record<string, unknown>)) {
    const n = narrowBotStatus(raw);
    if (n !== undefined) out[k] = n;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/** Extract the signals bundle leniently. Unknown / missing values stay undefined. */
export function extractReadinessSignals(value: unknown): ReadinessSignalsBundle | null {
  if (value === null || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  const signals = v['signals'];
  if (signals === null || typeof signals !== 'object') return null;
  const s = signals as Record<string, unknown>;
  return {
    crawler_access: extractCrawlerAccess(value) ?? undefined,
    ai_access: narrowAiAccess(s['ai_access']),
    agent_card: narrowTri(s['agent_card']),
    llms_txt: narrowTri(s['llms_txt']),
    robots_txt: narrowTri(s['robots_txt']),
    sitemap: narrowTri(s['sitemap']),
    structured_data: narrowTri(s['structured_data']),
    mcp: narrowTri(s['mcp']),
    api_catalog: narrowTri(s['api_catalog']),
    // ADR-0223 — ARD + UCP (lenient; missing/out-of-enum → undefined).
    ard: narrowTri(s['ard']),
    ucp: narrowTri(s['ucp']),
    // ADR-0225 — x402 (lenient). Sub-kind narrowed to closed enum.
    x402: narrowTri(s['x402']),
    ...(s['x402_kind'] === 'x402' || s['x402_kind'] === 'pay_per_crawl'
      ? { x402_kind: s['x402_kind'] as 'x402' | 'pay_per_crawl' }
      : {}),
    // ADR-0252 — PAP (lenient).
    pap: narrowTri(s['pap']),
    content_signals: narrowTri(s['content_signals']),
    ai_bots_status: narrowBotMap(v['ai_bots_status']),
    checked_at: typeof v['checked_at'] === 'string' ? (v['checked_at'] as string) : undefined,
  };
}
